import { PlatformType } from '@/types/authorization'
import { apiRequest } from '@/lib/httpClient'
import { authLog } from './debug'
import type {
  CheckCompanyResponse,
  CheckExistsResponse,
  ChildProductSubscriptionResponse,
  ClientSignupResponse,
  CouponItem,
  ExistingPaymentResponse,
  ForgotPasswordResponse,
  LoginResponse,
  MarkActiveResponse,
  ProcessNewEmailResponse,
  PricingOption,
  PricingOptionsResponse,
  StripeAttachResponse,
  UserSessionResponse,
  VerifySignupTokenResponse,
  VerifyResetPasswordResponse,
  VerifyLoginLinkResponse,
  SetPasswordResponse,
} from './types'

function normalizePricingResponse(response: PricingOptionsResponse): PricingOption[] {
  if (Array.isArray(response)) return response
  return Array.isArray(response?.data) ? response.data : []
}

export type { LoginPayload } from './types'

export async function login(payload: { userName: string; password: string; isUpdateEmail: boolean; updateEmailId?: string | null }) {
  authLog('API login request', {
    userName: payload.userName,
    isUpdateEmail: payload.isUpdateEmail,
  })
  return apiRequest<LoginResponse>('ui/user/confirmationEmail', {
    method: 'POST',
    open: true,
    body: payload,
  })
}

export async function verifyMfa(payload: { userName: string; password: string; session: string; code: string }) {
  authLog('API authenticator code request', { userName: payload.userName })
  return apiRequest<LoginResponse>('ui/user/mfa/verify', {
    method: 'POST',
    open: true,
    body: payload,
  })
}

export async function fetchUserSession(email: string) {
  return apiRequest<UserSessionResponse>(`user/login/${encodeURIComponent(email)}`, {
    method: 'GET',
  })
}

export async function forgotPassword(email: string) {
  authLog('API forgotPassword request', { email })
  return apiRequest<ForgotPasswordResponse>('ui/user/verifyemail', {
    method: 'POST',
    open: true,
    body: { email },
  })
}

export async function processNewEmail(payload: { id: string; password: string }) {
  authLog('API processNewEmail request', { id: payload.id })
  return apiRequest<ProcessNewEmailResponse>('user/ui/process/email', {
    method: 'POST',
    body: payload,
  })
}

export async function verifySignupToken(token: string) {
  authLog('API verifySignupToken request', { token })
  return apiRequest<VerifySignupTokenResponse>('ui/user/verifytoken', {
    method: 'GET',
    open: true,
    query: { token },
  })
}

export async function verifyResetPasswordLink(token: string) {
  authLog('API verifyResetPasswordLink request', { token })
  return apiRequest<VerifyResetPasswordResponse>('ui/user/verifyLink', {
    method: 'GET',
    open: true,
    query: { token },
  })
}

/**
 * Redeems an email-link 2FA. The token is the last part of the emailed URL (older links carry a
 * row id, which the API still accepts until they expire). Single use, so only call it on click.
 */
export async function confirmEmailLink(token: string) {
  authLog('API confirmEmailLink request')
  return apiRequest<VerifyLoginLinkResponse>('ui/user/2fa/confirm', {
    method: 'POST',
    open: true,
    body: { token },
  })
}

export async function setPasswordFromLink(email: string, password: string, token: string) {
  authLog('API setPasswordFromLink request', { email, token })
  return apiRequest<SetPasswordResponse>('ui/user/setPassword', {
    method: 'POST',
    open: true,
    body: { email, password, token },
  })
}

export async function xeroSignupEmail(firstName: string, surName: string, emailAddress: string, phone: string, xeroUserID: string) {
  return apiRequest<unknown>(`user/email/signupemail/${encodeURIComponent(firstName)}/${encodeURIComponent(surName)}/${encodeURIComponent(emailAddress)}/${encodeURIComponent(phone)}/${encodeURIComponent(xeroUserID)}`, {
    method: 'POST',
  })
}

export async function checkCompanyExists(payload: { Email: string; Company: string; isBas?: boolean }) {
  const response = await apiRequest<CheckCompanyResponse>('ui/create/companyexists', {
    method: 'POST',
    open: true,
    body: payload,
  })
  if (typeof response === 'boolean') return response
  return Boolean(response?.exist ?? response?.exists)
}

export async function checkEmailExists(payload: { Email: string; Company?: string; isBas?: boolean }) {
  const response = await apiRequest<CheckExistsResponse | boolean>('ui/create/exists', {
    method: 'POST',
    open: true,
    body: payload,
  })
  if (typeof response === 'boolean') {
    return { exist: response }
  }
  return response
}

export async function validateCoupon(payload: { Code: string; PlatformType?: number; Product?: string }) {
  const response = await apiRequest<CouponItem[] | { data?: CouponItem[] }>('ui/validate/Coupon', {
    method: 'POST',
    open: true,
    body: payload,
  })
  if (Array.isArray(response)) return response
  return Array.isArray(response?.data) ? response.data : []
}

// Pure payload readers live in ./coupon so a test can reach them without
// pulling the HTTP client in behind them. Re-exported so call sites are
// unchanged.
export { couponDetail, findCoupon } from './coupon'

const PRICE_LIST_PATHS: Partial<Record<PlatformType, string>> = {
  [PlatformType.FYI]: 'ui/signup/FYIPriceList',
  [PlatformType.Karbon]: 'ui/signup/KarbonPriceList',
  [PlatformType.BRWFM]: 'ui/signup/BRPriceList',
}

export async function getPriceList(platformType: number): Promise<PricingOption[]> {
  const path = PRICE_LIST_PATHS[platformType as PlatformType]
  if (!path) return []
  const response = await apiRequest<PricingOptionsResponse>(path, { method: 'GET', open: true })
  return normalizePricingResponse(response)
}

export async function clientSignup(payload: {
  clientForm: Record<string, unknown>
  platformType: number
  nocc?: boolean
}) {
  return apiRequest<ClientSignupResponse>('ui/create/signup', {
    method: 'POST',
    open: true,
    body: payload,
  })
}

export async function markActive(payload: { email: string; password: string; token: string }) {
  const response = await apiRequest<MarkActiveResponse>('ui/user/markActive', {
    method: 'POST',
    open: true,
    body: payload,
  })
  return {
    ...response,
    idToken: response.idToken || response.IdToken,
    refreshToken: response.refreshToken || response.RefreshToken,
    expiry: response.expiry ?? response.Expiry,
  }
}

export async function attachStripePayment(payload: {
  PaymentId: string
  CustomerId: string
  requireSubscription?: boolean
  subscribeToBAS?: boolean
}) {
  const response = await apiRequest<StripeAttachResponse>('user/ui/create/stripe', {
    method: 'POST',
    body: payload,
  })
  return {
    ...response,
    isSuccessful: response.isSuccessful ?? response.IsSuccessful,
    paymentMethod: response.paymentMethod || response.PaymentMethod,
    id: response.id || response.Id,
    customer: response.customer || response.Customer,
  }
}

export async function subscribeStripeProduct(payload: {
  productSubscription: {
    CustomerID: string
    PromoCode: string
    Users: string
    PaymentMethodId: string
    CustomNumOfUsers: number
  }
  platformType: number
  requireSubscription?: boolean
  subscribeToBAS?: boolean
}): Promise<unknown> {
  return apiRequest<unknown>('user/ui/stripe/productsubscriptions', {
    method: 'POST',
    body: payload,
  })
}

export async function attachExistingPayment(payload: { ClientId: string }) {
  const response = await apiRequest<ExistingPaymentResponse>('user/ui/stripe/attachExistingPayment', {
    method: 'POST',
    body: payload,
  })
  if (!response) return null
  const resolvedId =
    response.id ||
    response.Id ||
    response.paymentMethodId ||
    response.PaymentMethodId ||
    response.paymentMethod?.id ||
    response.paymentMethod?.Id ||
    response.PaymentMethod?.id ||
    response.PaymentMethod?.Id
  return resolvedId ? { id: resolvedId } : null
}

export async function childProductSubscription(payload: {
  productSubscription: {
    CustomerID: string
    PromoCode: string
    Users: string
    PaymentMethodId: string
    CustomNumOfUsers: number
    ClientId?: string
  }
  platformType: number
}) {
  const response = await apiRequest<ChildProductSubscriptionResponse>('user/ui/stripe/childProductSubscription', {
    method: 'POST',
    body: payload,
  })

  if (response === false) return false
  if (response === true) return true
  if (!response || typeof response !== 'object') return false

  if (response.success !== undefined) return Boolean(response.success)

  const isSuccessful = response.isSuccessful ?? response.IsSuccessful
  if (isSuccessful !== undefined) return Boolean(isSuccessful)

  if (response.response === 'Ok') return true
  if (response.result?.response === 'Ok') return true

  if (response.isCompletedSuccessfully === true) return true
  if (response.isFaulted === true) return false

  // Legacy Angular only treated literal `false` as failure and proceeded for
  // any object payload. Keep that compatibility for wrapped async responses.
  return true
}

export async function exchangeBRAccessToken(code: string) {
  return apiRequest<{ access_token?: string; refresh_token?: string; expires_in?: number }>('ui/create/BRaccesstoken', {
    method: 'POST',
    open: true,
    body: { Code: code },
  })
}

export async function exchangeMyobAccessToken({
  code,
  platformId,
  businessId,
  businessName,
}: {
  code: string
  platformId: string
  businessId?: string
  businessName?: string
}) {
  return apiRequest('ui/create/MYobaccesstoken', {
    method: 'POST',
    open: true,
    body: {
      Code: code,
      PlatformId: platformId,
      ...(businessId ? { BusinessId: businessId } : {}),
      ...(businessName ? { BusinessName: businessName } : {}),
    },
  })
}

// QuickBooks differs from BlueRock: this endpoint is authenticated (the user's
// idToken is attached automatically because we omit `open`) and saves the tokens
// server-side. A 200 means the connection is stored, so callers do not need to
// follow up with updatePlatformTokens.
export async function exchangeQuickBooksAccessToken(
  code: string,
  realmId: string,
  platformId: string,
  clientId: string,
) {
  return apiRequest('platform/ui/create/QuickBooksaccesstoken', {
    method: 'POST',
    body: { Code: code, RealmId: realmId, PlatformId: platformId, clientId: clientId },
  })
}

export async function updatePlatformTokens(payload: {
  accessToken: string
  refreshToken: string
  expireIn: number
  platformId: string | null
  clientId: string
}) {
  return apiRequest('platform/ui/tokens/update', {
    method: 'POST',
    body: payload,
  })
}

export async function checkPlatformTokens(platformId: string) {
  return apiRequest<boolean>('platform/ui/tokens/check', {
    method: 'GET',
    query: { platformId },
  })
}

export async function refreshPlatform(platformId: string) {
  return apiRequest('platform/ui/refresh', {
    method: 'POST',
    body: { platformId },
  })
}

export async function fetchPlatformsByClient(clientId: string) {
  return apiRequest<{ platforms?: Array<{ platformId: string }> }>('platform/ui/list/grid', {
    method: 'GET',
    query: { clientId, filterByCompanyId: false },
  })
}

export async function initPlatformEdit(platformId: string) {
  return apiRequest<{ platform?: Record<string, unknown> }>('platform/ui/edit/initialise', {
    method: 'GET',
    query: { platformId },
  })
}

export async function updatePlatformCredentials(payload: Record<string, unknown>) {
  return apiRequest('platform/ui/edit/save', {
    method: 'POST',
    body: payload,
  })
}
